import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useOnlineStatus } from './useOnlineStatus'
import {
  contarTodosPendentes,
  listarPendentes,
  listarRDOsPendentes,
  marcarErroPendente,
  marcarRDOErro,
  marcarSincronizado,
  deletarRDOSincronizado,
  type FotoPendentePayload,
  type FVMPendentePayload,
  type FVSPendentePayload,
  type PendenteRecord,
  type RDOPendenteRecord,
  type TarefaPendentePayload,
} from '@/lib/offlineDB'

const BUCKET_FOTOS = 'fotos-obras'

type Progresso = { etapa: string; atual: number; total: number } | null

/**
 * useSyncManager — orquestra a sincronização de TODOS os módulos offline.
 * Ordem: Tarefas → RDOs → FVS → FVM → Fotos (mais pesadas por último).
 *
 * Anti-duplicata é responsabilidade de cada syncer individual.
 */
export function useSyncManager() {
  const isOnline = useOnlineStatus()
  const [pendentes, setPendentes] = useState(0)
  const [porModulo, setPorModulo] = useState<Record<string, number>>({})
  const [syncing, setSyncing] = useState(false)
  const [progresso, setProgresso] = useState<Progresso>(null)
  const lastRunRef = useRef(0)
  const syncingRef = useRef(false)
  const mountedRef = useRef(true)
  const refreshTimerRef = useRef<number | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const refreshCount = useCallback(async () => {
    try {
      const { total, porStore } = await contarTodosPendentes()
      setPendentes(total)
      setPorModulo(porStore as any)
    } catch {
      setPendentes(0); setPorModulo({})
    }
  }, [])

  // ─── Syncers ──────────────────────────────────
  const syncTarefas = async (): Promise<{ ok: number; fail: number }> => {
    let ok = 0, fail = 0
    const list = await listarPendentes<TarefaPendentePayload>('tarefas_pendentes')
    for (const rec of list) {
      const d = rec.data
      try {
        const updates: Record<string, unknown> = { status: d.novo_status }
        if (typeof d.progresso === 'number') updates.progresso = d.progresso
        if (d.data_conclusao) updates.data_conclusao = d.data_conclusao
        const { error } = await supabase
          .from('tarefas')
          .update(updates)
          .eq('id', d.tarefa_id)
          .eq('company_id', d.company_id)
        if (error) {
          await marcarErroPendente('tarefas_pendentes', rec.id, error.message)
          fail++
        } else {
          await marcarSincronizado('tarefas_pendentes', rec.id)
          ok++
        }
      } catch (e: any) {
        await marcarErroPendente('tarefas_pendentes', rec.id, e?.message || 'erro')
        fail++
      }
    }
    return { ok, fail }
  }

  const syncRDOs = async (): Promise<{ ok: number; fail: number }> => {
    let ok = 0, fail = 0
    const list: RDOPendenteRecord[] = await listarRDOsPendentes()
    for (const rec of list) {
      try {
        // Anti-duplicata: se já existe RDO com mesma obra+data+autor,
        // compara o conteúdo antes de decidir. Descartar só é seguro quando
        // o registro local é uma retransmissão do mesmo conteúdo — caso
        // contrário seria perda silenciosa de dados de campo.
        const { data: existing } = await supabase
          .from('diario_obra')
          .select('id, servicos_executados, observacoes, total_trabalhadores')
          .eq('obra_id', rec.obra_id)
          .eq('data_registro', rec.data_registro)
          .eq('criado_por', rec.criado_por)
          .maybeSingle()
        if (existing?.id) {
          const mesmoConteudo =
            (existing.servicos_executados ?? '') === (rec.servicos_executados ?? '') &&
            (existing.observacoes ?? '') === (rec.observacoes ?? '') &&
            (existing.total_trabalhadores ?? 0) === (rec.total_trabalhadores ?? 0)
          if (mesmoConteudo) {
            // Retransmissão do mesmo RDO — seguro descartar
            await deletarRDOSincronizado(rec.id); ok++
          } else {
            // Conteúdo divergente — preserva o registro local e sinaliza
            await marcarRDOErro(
              rec.id,
              'Conflito: já existe um RDO nesta data com conteúdo diferente. ' +
              'Seus dados foram preservados — abra o Diário de Obra para revisar.'
            )
            fail++
          }
          continue
        }

        const { id, status, criado_offline_em, ultima_tentativa_em, ultimo_erro, linkedTasks, ...payload } = rec as any
        const { data: inserted, error } = await supabase
          .from('diario_obra')
          .insert({ ...payload, status_rdo: 'rascunho' })
          .select('id')
          .maybeSingle()

        if (error) {
          if (error.code === '23505') {
            // Corrida: outro cliente inseriu entre a checagem e o insert.
            // Não é seguro descartar sem comparar — preserva e sinaliza.
            await marcarRDOErro(
              rec.id,
              'Conflito: já existe um RDO nesta data com conteúdo diferente. ' +
              'Seus dados foram preservados — abra o Diário de Obra para revisar.'
            )
            fail++
          } else {
            await marcarRDOErro(rec.id, error.message); fail++
          }
        } else {
          // Sync linkedTasks → rdo_tarefas (best-effort)
          if (inserted?.id && linkedTasks?.length) {
            const rows = (linkedTasks as any[]).map((lt: any) => ({
              rdo_id: inserted.id,
              obra_id: rec.obra_id,
              tarefa_id: lt.tarefaId ?? null,
              nome_manual: lt.nome,
              tipo: lt.tipo,
              percentual: lt.percentual,
              company_id: lt.company_id ?? rec.company_id,
              criado_por: rec.criado_por,
            }))
            await supabase.from('rdo_tarefas').insert(rows).then(({ error: e }) => {
              if (e) console.warn('[useSyncManager] rdo_tarefas insert error:', e.message)
            })
          }
          await deletarRDOSincronizado(rec.id); ok++
        }
      } catch (e: any) {
        await marcarRDOErro(rec.id, e?.message || 'erro'); fail++
      }
    }
    return { ok, fail }
  }

  const syncFVS = async (): Promise<{ ok: number; fail: number }> => {
    let ok = 0, fail = 0
    const list = await listarPendentes<FVSPendentePayload>('fvs_pendentes')
    for (const rec of list) {
      const d = rec.data
      try {
        // anti-duplicata: mesmo título + obra + autor + dia
        const { data: existing } = await supabase
          .from('fvs').select('id, itens')
          .eq('obra_id', d.obra_id)
          .eq('criado_por', d.criado_por)
          .eq('titulo', d.titulo)
          .gte('created_at', d.data_verificacao + 'T00:00:00')
          .lte('created_at', d.data_verificacao + 'T23:59:59')
          .maybeSingle()
        if (existing?.id) {
          // Só descarta se o checklist for idêntico (retransmissão);
          // conteúdo divergente é preservado para revisão manual.
          if (JSON.stringify(existing.itens ?? null) === JSON.stringify(d.itens ?? null)) {
            await marcarSincronizado('fvs_pendentes', rec.id); ok++
          } else {
            await marcarErroPendente('fvs_pendentes', rec.id,
              'Conflito: já existe FVS com este título nesta data e conteúdo diferente. Dados preservados.')
            fail++
          }
          continue
        }
        const { data: ins, error } = await supabase.from('fvs').insert({
          obra_id: d.obra_id, company_id: d.company_id, criado_por: d.criado_por,
          titulo: d.titulo, disciplina: d.disciplina, servico: d.servico || '',
          local_obra: d.pavimento || null, ambiente: d.ambiente || null,
          andar: d.pavimento || null, unidade: d.unidade || null,
          data_verificacao: d.data_verificacao,
          responsavel: d.responsavel || null, responsavel_id: d.responsavel_id || null,
          status: 'em_preenchimento',
          itens: d.itens,
        } as any).select('id').single()
        if (error || !ins) { await marcarErroPendente('fvs_pendentes', rec.id, error?.message || 'falha'); fail++ }
        else { await marcarSincronizado('fvs_pendentes', rec.id); ok++ }
      } catch (e: any) {
        await marcarErroPendente('fvs_pendentes', rec.id, e?.message || 'erro'); fail++
      }
    }
    return { ok, fail }
  }

  const syncFVM = async (): Promise<{ ok: number; fail: number }> => {
    let ok = 0, fail = 0
    const list = await listarPendentes<FVMPendentePayload>('fvm_pendentes')
    for (const rec of list) {
      const d = rec.data
      try {
        const { data: existing } = await supabase
          .from('fvm_fichas').select('id, percentual_conformidade, status')
          .eq('obra_id', d.obra_id)
          .eq('criado_por', d.criado_por)
          .eq('material', d.material)
          .gte('created_at', d.data_inspecao + 'T00:00:00')
          .lte('created_at', d.data_inspecao + 'T23:59:59')
          .maybeSingle()
        if (existing?.id) {
          // Só descarta retransmissões idênticas; conteúdo divergente é preservado.
          if (existing.percentual_conformidade === d.percentual_conformidade &&
              existing.status === d.status) {
            await marcarSincronizado('fvm_pendentes', rec.id); ok++
          } else {
            await marcarErroPendente('fvm_pendentes', rec.id,
              'Conflito: já existe FVM deste material nesta data com resultado diferente. Dados preservados.')
            fail++
          }
          continue
        }
        const { error } = await supabase.from('fvm_fichas').insert({
          obra_id: d.obra_id, company_id: d.company_id, criado_por: d.criado_por,
          titulo: d.titulo, fornecedor: d.fornecedor, material: d.material,
          lote: d.lote || null,
          descricao: [d.nota_fiscal ? `NF: ${d.nota_fiscal}` : '', d.observacoes || ''].filter(Boolean).join(' | ') || null,
          criticidade: d.criticidade, status: d.status,
          percentual_conformidade: d.percentual_conformidade,
          data_inspecao: d.data_inspecao,
        } as any)
        if (error) { await marcarErroPendente('fvm_pendentes', rec.id, error.message); fail++ }
        else { await marcarSincronizado('fvm_pendentes', rec.id); ok++ }
      } catch (e: any) {
        await marcarErroPendente('fvm_pendentes', rec.id, e?.message || 'erro'); fail++
      }
    }
    return { ok, fail }
  }

  const syncFotos = async (): Promise<{ ok: number; fail: number }> => {
    let ok = 0, fail = 0
    const list = await listarPendentes<FotoPendentePayload>('fotos_pendentes')

    // Upload em paralelo com limite de 3 simultâneos para não saturar a rede
    const CONCURRENCY = 3
    const syncOneFoto = async (rec: PendenteRecord<FotoPendentePayload>): Promise<'ok' | 'fail'> => {
      const d = rec.data
      try {
        const res = await fetch(d.base64)
        const blob = await res.blob()
        const ext = (d.nome_arquivo.split('.').pop() || 'jpg').toLowerCase()
        // P0-S09: company_id como primeiro segmento (isolamento de tenant)
        const path = `${d.company_id}/${d.obra_id}/${d.criado_por}/${Date.now()}_offline.${ext}`
        const { error: upErr } = await supabase.storage.from(BUCKET_FOTOS)
          .upload(path, blob, { contentType: d.mime_type, upsert: false })
        if (upErr) { await marcarErroPendente('fotos_pendentes', rec.id, upErr.message); return 'fail' }
        const { error: dbErr } = await supabase.from('fotos').insert({
          obra_id: d.obra_id, company_id: d.company_id, criado_por: d.criado_por,
          storage_path: path, storage_bucket: BUCKET_FOTOS,
          url_publica: null,
          mime_type: d.mime_type, tamanho_bytes: d.tamanho_bytes,
          titulo: d.titulo, categoria: d.categoria || 'avanco_obra',
          descricao: d.descricao || null, local_obra: d.local_obra || null, andar: d.andar || null,
        } as any)
        if (dbErr) {
          await supabase.storage.from(BUCKET_FOTOS).remove([path])
          await marcarErroPendente('fotos_pendentes', rec.id, dbErr.message); return 'fail'
        }
        await marcarSincronizado('fotos_pendentes', rec.id); return 'ok'
      } catch (e: any) {
        await marcarErroPendente('fotos_pendentes', rec.id, e?.message || 'erro'); return 'fail'
      }
    }

    for (let i = 0; i < list.length; i += CONCURRENCY) {
      const batch = list.slice(i, i + CONCURRENCY)
      const results = await Promise.all(batch.map(syncOneFoto))
      results.forEach(r => r === 'ok' ? ok++ : fail++)
    }
    return { ok, fail }
  }

  // ─── Orquestrador ─────────────────────────────
  // syncingRef evita que `syncing` (state) entre nas deps e recrie runSync
  // durante a própria execução, causando re-trigger do useEffect de auto-sync.
  const runSync = useCallback(async (force = false) => {
    const now = Date.now()
    if (!force && now - lastRunRef.current < 5000) return
    lastRunRef.current = now
    if (!isOnline || syncingRef.current) return

    const { total } = await contarTodosPendentes()
    if (total === 0) { await refreshCount(); return }

    syncingRef.current = true
    setSyncing(true)
    let totalOk = 0, totalFail = 0
    const etapas: { nome: string; fn: () => Promise<{ ok: number; fail: number }> }[] = [
      { nome: 'Tarefas', fn: syncTarefas },
      { nome: 'RDOs',    fn: syncRDOs },
      { nome: 'FVS',     fn: syncFVS },
      { nome: 'FVM',     fn: syncFVM },
      { nome: 'Fotos',   fn: syncFotos },
    ]
    for (let i = 0; i < etapas.length; i++) {
      const e = etapas[i]
      setProgresso({ etapa: e.nome, atual: i + 1, total: etapas.length })
      try {
        const r = await e.fn()
        totalOk += r.ok; totalFail += r.fail
      } catch { totalFail++ }
    }
    syncingRef.current = false
    if (!mountedRef.current) return
    setProgresso(null)
    setSyncing(false)
    await refreshCount()

    if (totalOk > 0) toast.success(`Sincronização completa — ${totalOk} item(ns) enviados`)
    if (totalFail > 0) {
      toast.warning(`${totalFail} item(ns) com falha`, {
        action: { label: 'Tentar novamente', onClick: () => void runSync(true) },
      })
    }
  }, [isOnline, refreshCount])

  // Auto-sync ao abrir e ao voltar online
  useEffect(() => {
    void refreshCount()
    if (isOnline) void runSync()
  }, [isOnline, refreshCount, runSync])

  // Polling leve do contador — jitter de até 3s para evitar que todos os
  // clientes sincronizem exatamente ao mesmo tempo e sobrecarreguem o banco
  useEffect(() => {
    if (refreshTimerRef.current) window.clearInterval(refreshTimerRef.current)
    const jitter = Math.random() * 3000
    refreshTimerRef.current = window.setInterval(() => { void refreshCount() }, 8000 + jitter)
    return () => {
      if (refreshTimerRef.current) window.clearInterval(refreshTimerRef.current)
    }
  }, [refreshCount])

  return { pendentes, porModulo, syncing, progresso, runSync, refreshCount, isOnline }
}
